import * as React from "react";
import {Button, Checkbox, Header, Icon, Label, Modal, Popup} from "semantic-ui-react";
import {CompanyConfigurationChange} from "../../../../../../generated/api";
import {formatDate} from "../../../../../../format";
import {updateConfig, workingConfig} from "../../ConfigSignal";

const YAML = require("yaml");
const JP = require("jsonpath");

export interface ChangeDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    change: CompanyConfigurationChange | null;
    ticketNrs?: string[];
    onOpenHistory?: (historyId: string) => void;
}

function normalizeValue(value: any): string {
    if (value === null || value === undefined) {
        return "-";
    }
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "object") {
        try {
            return JSON.stringify(value, null, 2);
        } catch {
            return String(value);
        }
    }
    return String(value);
}

function toYaml(value: any): string {
    if (value === null || value === undefined) {
        return "-";
    }
    try {
        return YAML.stringify(value);
    } catch {
        return normalizeValue(value);
    }
}

function isJsonObject(value: any): boolean {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function tryParseJsonObjectString(value: any): any | null {
    if (typeof value !== "string") {
        return null;
    }
    const trimmed = value.trim();
    if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) {
        return null;
    }
    try {
        const parsed = JSON.parse(trimmed);
        return isJsonObject(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function getStructuredValue(value: any): any {
    const parsedObject = tryParseJsonObjectString(value);
    return parsedObject ?? value;
}

function deepEqual(left: any, right: any): boolean {
    if (Object.is(left, right)) {
        return true;
    }
    if (typeof left !== typeof right) {
        return false;
    }
    if (left === null || right === null) {
        return false;
    }
    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
            return false;
        }
        for (let i = 0; i < left.length; i++) {
            if (!deepEqual(left[i], right[i])) {
                return false;
            }
        }
        return true;
    }
    if (typeof left === "object") {
        const leftKeys = Object.keys(left);
        const rightKeys = Object.keys(right);
        if (leftKeys.length !== rightKeys.length) {
            return false;
        }
        for (const key of leftKeys) {
            if (!Object.prototype.hasOwnProperty.call(right, key) || !deepEqual(left[key], right[key])) {
                return false;
            }
        }
        return true;
    }
    return false;
}

interface DiffSegment {
    text: string;
    changed: boolean;
}

interface DiffLine {
    text: string;
    changed: boolean;
}

interface DiffOp {
    type: "equal" | "delete" | "insert";
    text: string;
}

interface ChangePair {
    oldSegments: DiffSegment[];
    newSegments: DiffSegment[];
    hasContext: boolean;
}

interface LineDiffResult {
    operations: DiffOp[];
    changed: boolean;
}

function buildLineDiff(oldValue: any, newValue: any): LineDiffResult {
    const oldText = normalizeValue(oldValue);
    const newText = normalizeValue(newValue);

    if (oldText === newText) {
        return {
            operations: [{type: "equal", text: oldText}],
            changed: false
        };
    }

    const oldLinesRaw = oldText.split("\n");
    const newLinesRaw = newText.split("\n");
    const n = oldLinesRaw.length;
    const m = newLinesRaw.length;

    const dp: number[][] = Array.from({length: n + 1}, () => Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            if (oldLinesRaw[i] === newLinesRaw[j]) {
                dp[i][j] = dp[i + 1][j + 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
            }
        }
    }

    const operations: DiffOp[] = [];

    let i = 0;
    let j = 0;
    while (i < n && j < m) {
        if (oldLinesRaw[i] === newLinesRaw[j]) {
            operations.push({type: "equal", text: oldLinesRaw[i]});
            i++;
            j++;
            continue;
        }
        if (dp[i + 1][j] >= dp[i][j + 1]) {
            operations.push({type: "delete", text: oldLinesRaw[i]});
            i++;
        } else {
            operations.push({type: "insert", text: newLinesRaw[j]});
            j++;
        }
    }

    while (i < n) {
        operations.push({type: "delete", text: oldLinesRaw[i]});
        i++;
    }
    while (j < m) {
        operations.push({type: "insert", text: newLinesRaw[j]});
        j++;
    }

    return {
        operations,
        changed: true
    };
}

function linesToSegments(lines: DiffLine[]): DiffSegment[] {
    if (lines.length === 0) {
        return [{text: "", changed: false}];
    }
    const segments: DiffSegment[] = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const text = i < lines.length - 1 ? `${line.text}\n` : line.text;
        const previous = segments[segments.length - 1];
        if (!previous || previous.changed !== line.changed) {
            segments.push({text, changed: line.changed});
        } else {
            previous.text += text;
        }
    }
    return segments;
}

function sliceToChangePair(ops: DiffOp[]): ChangePair {
    const oldLines: DiffLine[] = [];
    const newLines: DiffLine[] = [];

    ops.forEach((op) => {
        if (op.type === "equal") {
            oldLines.push({text: op.text, changed: false});
            newLines.push({text: op.text, changed: false});
        } else if (op.type === "delete") {
            oldLines.push({text: op.text, changed: true});
        } else {
            newLines.push({text: op.text, changed: true});
        }
    });

    const oldSegments = oldLines.length > 0 ? linesToSegments(oldLines) : [{text: "-", changed: false}];
    const newSegments = newLines.length > 0 ? linesToSegments(newLines) : [{text: "-", changed: false}];
    const hasContext = ops.some((op) => op.type === "equal");

    return {oldSegments, newSegments, hasContext};
}

function buildChangePairs(operations: DiffOp[], withContext: boolean, contextLines = 5): ChangePair[] {
    const ranges: Array<{ start: number; end: number }> = [];
    let idx = 0;
    while (idx < operations.length) {
        if (operations[idx].type === "equal") {
            idx++;
            continue;
        }
        const start = idx;
        while (idx < operations.length && operations[idx].type !== "equal") {
            idx++;
        }
        const end = idx - 1;
        ranges.push({start, end});
    }

    if (ranges.length === 0) {
        return [];
    }

    if (!withContext) {
        return ranges.map((range) => sliceToChangePair(operations.slice(range.start, range.end + 1)));
    }

    const windows = ranges.map((range) => ({
        start: Math.max(0, range.start - contextLines),
        end: Math.min(operations.length - 1, range.end + contextLines)
    }));

    const merged: Array<{ start: number; end: number }> = [];
    windows.forEach((window) => {
        const previous = merged[merged.length - 1];
        if (!previous || window.start > previous.end + 1) {
            merged.push({...window});
        } else {
            previous.end = Math.max(previous.end, window.end);
        }
    });

    return merged.map((range) => sliceToChangePair(operations.slice(range.start, range.end + 1)));
}

function buildSingleFullPair(operations: DiffOp[]): ChangePair {
    return sliceToChangePair(operations);
}

function DiffBlock({segments, color}: {
    segments: DiffSegment[];
    color: "red" | "green"
}) {
    const isGreen = color === "green";
    const bg = isGreen ? "#f0fdf4" : "#fff1f2";
    const borderColor = isGreen ? "#16a34a" : "#e11d48";
    const labelColor = isGreen ? "#14532d" : "#881337";
    const highlightBg = isGreen ? "#bbf7d0" : "#fecdd3";
    const title = isGreen ? "NEW" : "OLD";

    return (
        <div style={{flex: 1, minWidth: 0}}>
            <div style={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: labelColor,
                marginBottom: "4px"
            }}>
                {title}
            </div>
            <pre style={{
                margin: 0,
                padding: "10px 12px",
                backgroundColor: bg,
                border: `1px solid ${borderColor}`,
                borderRadius: "8px",
                fontSize: "12px",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: labelColor,
                minHeight: "48px",
                lineHeight: 1.6
            }}>
                {segments.map((segment, index) => (
                    <span
                        key={`${color}-${index}`}
                        style={segment.changed ? {
                            backgroundColor: highlightBg,
                            borderRadius: "3px",
                            padding: "0 2px",
                            fontWeight: 700
                        } : undefined}
                    >
                        {segment.text}
                    </span>
                ))}
            </pre>
        </div>
    );
}

function InfoRow({label, children}: { label: string; children: React.ReactNode }) {
    return (
        <div style={{
            display: "flex",
            gap: "12px",
            padding: "8px 0",
            borderBottom: "1px solid #f1f5f9",
            alignItems: "flex-start"
        }}>
            <span style={{
                minWidth: "100px",
                fontSize: "11px",
                fontWeight: 700,
                color: "#64748b",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                paddingTop: "2px"
            }}>
                {label}
            </span>
            <span style={{
                fontSize: "13px",
                color: "#0f172a",
                wordBreak: "break-word",
                flex: 1,
                fontFamily: "inherit"
            }}>
                {children}
            </span>
        </div>
    );
}

export function ChangeDetailModal({isOpen, onClose, change, ticketNrs = [], onOpenHistory}: ChangeDetailModalProps) {
    const [yamlView, setYamlView] = React.useState(false);
    const [focusOnChangeOnly, setFocusOnChangeOnly] = React.useState(false);
    const [isApplyingValue, setIsApplyingValue] = React.useState(false);
    const [applyError, setApplyError] = React.useState<string | null>(null);
    const [applyVersion, setApplyVersion] = React.useState(0);

    const structuredOldValue = change ? getStructuredValue(change.oldValue) : null;
    const structuredNewValue = change ? getStructuredValue(change.value) : null;
    const canToggleFormat = !!change && (isJsonObject(structuredOldValue) || isJsonObject(structuredNewValue));
    const activePath = (change?.path || "").trim();

    const pathState = React.useMemo(() => {
        if (!activePath) {
            return {reason: "Path is missing in this change entry.", currentValue: undefined};
        }
        if (!workingConfig.value) {
            return {reason: "No workingConfig is loaded.", currentValue: undefined};
        }
        try {
            const configObj = JSON.parse(workingConfig.value);
            const matches = JP.query(configObj, activePath);
            if (!matches || matches.length === 0) {
                return {reason: "Path does not exist in the current workingConfig.", currentValue: undefined};
            }
            return {reason: null, currentValue: JP.value(configObj, activePath)};
        } catch (e) {
            return {reason: `Could not read workingConfig/path: ${String((e as any)?.message || e)}`, currentValue: undefined};
        }
    }, [activePath, applyVersion, workingConfig.value]);

    const oldValueDisabledReason = React.useMemo(() => {
        if (pathState.reason) {
            return pathState.reason;
        }
        if (deepEqual(pathState.currentValue, structuredOldValue)) {
            return "Current value already equals the old value.";
        }
        return null;
    }, [pathState, structuredOldValue]);

    const newValueDisabledReason = React.useMemo(() => {
        if (pathState.reason) {
            return pathState.reason;
        }
        if (deepEqual(pathState.currentValue, structuredNewValue)) {
            return "Current value already equals the new value.";
        }
        return null;
    }, [pathState, structuredNewValue]);

    React.useEffect(() => {
        if (!canToggleFormat && yamlView) {
            setYamlView(false);
        }
    }, [canToggleFormat, yamlView]);

    React.useEffect(() => {
        setApplyError(null);
    }, [change?.historyId, isOpen]);

    async function applyValueToWorkingConfig(valueToApply: any) {
        if (!change || !activePath || !workingConfig.value) {
            return;
        }
        setIsApplyingValue(true);
        setApplyError(null);
        try {
            const configObj = JSON.parse(workingConfig.value);
            const matches = JP.query(configObj, activePath);
            if (!matches || matches.length === 0) {
                setApplyError("Could not apply value: path was not found in workingConfig.");
                return;
            }
            JP.value(configObj, activePath, valueToApply);
            await updateConfig(JSON.stringify(configObj, null, 4), true, false);
            setApplyVersion((prev) => prev + 1);
        } catch (e) {
            setApplyError(`Could not apply value: ${String((e as any)?.message || e)}`);
        } finally {
            setIsApplyingValue(false);
        }
    }

    function renderApplyButton(label: string, color: "orange" | "green", valueToApply: any, disabledReason: string | null) {
        const disabled = !!disabledReason || isApplyingValue;
        const button = (
            <Button
                color={color}
                disabled={disabled}
                loading={isApplyingValue}
                onClick={() => applyValueToWorkingConfig(valueToApply).then()}
            >
                {label}
            </Button>
        );

        if (!disabledReason) {
            return button;
        }

        return (
            <Popup
                trigger={<span style={{display: "inline-block"}}>{button}</span>}
                content={disabledReason}
                position="top center"
                size="mini"
            />
        );
    }

    const useYaml = canToggleFormat && yamlView;
    const oldRenderedFull = useYaml ? toYaml(structuredOldValue) : normalizeValue(structuredOldValue);
    const newRenderedFull = useYaml ? toYaml(structuredNewValue) : normalizeValue(structuredNewValue);
    const fullDiff = React.useMemo(
        () => buildLineDiff(oldRenderedFull, newRenderedFull),
        [oldRenderedFull, newRenderedFull]
    );
    const plainPairs = React.useMemo(
        () => buildChangePairs(fullDiff.operations, false, 5),
        [fullDiff]
    );
    const contextPairs = React.useMemo(
        () => buildChangePairs(fullDiff.operations, true, 5),
        [fullDiff]
    );
    const fullPair = React.useMemo(
        () => buildSingleFullPair(fullDiff.operations),
        [fullDiff]
    );
    const canToggleFocus = fullDiff.changed && contextPairs.some((pair) => pair.hasContext);
    const displayedPairs = focusOnChangeOnly && canToggleFocus ? contextPairs : [fullPair];

    React.useEffect(() => {
        if (!canToggleFocus && focusOnChangeOnly) {
            setFocusOnChangeOnly(false);
        }
    }, [canToggleFocus, focusOnChangeOnly]);

    if (!change) return null;

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            size="fullscreen"
            closeIcon
        >
            <Header icon="history" content="Change Details"/>
            <Modal.Content scrolling>

                {/* Meta Info */}
                <div style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    marginBottom: "20px"
                }}>
                    <InfoRow label="Path">
                        <code style={{
                            fontSize: "12px",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            color: "#475569"
                        }}>
                            {change.path || "-"}
                        </code>
                    </InfoRow>
                    <InfoRow label="User">
                        <strong>{change.userName || "-"}</strong>
                    </InfoRow>
                    <InfoRow label="Timestamp">
                        {formatDate(change.timestamp)}
                    </InfoRow>
                    <InfoRow label="Comment">
                        {change.comment || <span style={{color: "#94a3b8", fontStyle: "italic"}}>no comment</span>}
                    </InfoRow>
                    {ticketNrs.length > 0 && (
                        <InfoRow label="Tickets">
                            <span style={{display: "flex", flexWrap: "wrap", gap: "4px"}}>
                                {ticketNrs.map((ticketNr, index) => {
                                    const url = `https://gitlab.logenios.com/LOGENIOS/xconvert/-/work_items/${ticketNr}`;
                                    return (
                                        <React.Fragment key={`${ticketNr}-${index}`}>
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{color: "#2563eb", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "4px"}}
                                            >
                                                #{ticketNr}
                                                <Icon name="external" style={{margin: 0, fontSize: "10px"}}/>
                                            </a>
                                            {index < ticketNrs.length - 1 ? <span style={{color: "#94a3b8"}}>,</span> : null}
                                        </React.Fragment>
                                    );
                                })}
                            </span>
                        </InfoRow>
                    )}
                    {(change as any).changeMethod && (
                        <InfoRow label="Method">
                            <Label size="tiny" color="blue">{(change as any).changeMethod}</Label>
                        </InfoRow>
                    )}
                    {change.historyId && (
                        <InfoRow label="History ID">
                            <button
                                type="button"
                                onClick={() => onOpenHistory && onOpenHistory(change.historyId as string)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    margin: 0,
                                    color: "#2563eb",
                                    cursor: onOpenHistory ? "pointer" : "default",
                                    fontSize: "11px",
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
                                }}
                            >
                                {change.historyId}
                            </button>
                        </InfoRow>
                    )}
                </div>

                {/* Diff Section */}
                <div style={{marginBottom: "8px"}}>
                    {canToggleFormat && (
                        <p style={{margin: "0 0 10px 0", color: "#475569", fontSize: "12px"}}>
                            JSON
                            <Checkbox
                                style={{margin: "0 8px"}}
                                toggle
                                checked={yamlView}
                                onChange={() => setYamlView(!yamlView)}
                            />
                            YAML
                        </p>
                    )}
                    {canToggleFocus && (
                        <p style={{margin: "0 0 10px 0", color: "#475569", fontSize: "12px", display: "flex", alignItems: "center", gap: "8px"}}>
                            Full values
                            <Checkbox
                                toggle
                                checked={focusOnChangeOnly}
                                onChange={() => setFocusOnChangeOnly(!focusOnChangeOnly)}
                            />
                            Changes with context (+/-5 lines)
                        </p>
                    )}
                    <h4 style={{
                        margin: "0 0 12px 0",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#334155",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        <Icon name="exchange" style={{margin: 0}}/>
                        Change
                    </h4>

                    {!fullDiff.changed ? (
                        <div style={{
                            padding: "12px",
                            background: "#f1f5f9",
                            borderRadius: "8px",
                            color: "#64748b",
                            fontSize: "13px",
                            fontStyle: "italic"
                        }}>
                            No difference between old and new value.
                        </div>
                    ) : (
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px"
                        }}>
                            {displayedPairs.map((pair, index) => (
                                <div key={`change-pair-${index}`} style={{
                                    display: "flex",
                                    gap: "12px",
                                    alignItems: "stretch",
                                    flexWrap: "wrap"
                                }}>
                                    <div style={{display: "flex", flexDirection: "column", gap: "6px", flex: 1, minWidth: 0}}>
                                        {index === 0 && (
                                            <div style={{display: "flex", justifyContent: "flex-start"}}>
                                                {renderApplyButton("use old value", "orange", structuredOldValue, oldValueDisabledReason)}
                                            </div>
                                        )}
                                        <DiffBlock
                                            segments={pair.oldSegments}
                                            color="red"
                                        />
                                    </div>
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "0 4px",
                                        minWidth: "32px"
                                    }}>
                                        <Icon
                                            name="arrow right"
                                            size="large"
                                            style={{color: "#94a3b8", margin: 0}}
                                        />
                                    </div>
                                    <div style={{display: "flex", flexDirection: "column", gap: "6px", flex: 1, minWidth: 0}}>
                                        {index === 0 && (
                                            <div style={{display: "flex", justifyContent: "flex-start"}}>
                                                {renderApplyButton("use new value", "green", structuredNewValue, newValueDisabledReason)}
                                            </div>
                                        )}
                                        <DiffBlock
                                            segments={pair.newSegments}
                                            color="green"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {applyError ? (
                        <div style={{color: "#b91c1c", fontSize: "12px", marginTop: "8px"}}>{applyError}</div>
                    ) : null}
                    {focusOnChangeOnly && canToggleFocus ? (
                        <div style={{color: "#64748b", fontSize: "11px", marginTop: "6px"}}>
                            Showing only the changed region with 5 lines of context.
                        </div>
                    ) : null}
                </div>

            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>
                    <Icon name="close"/> Close
                </Button>
            </Modal.Actions>
        </Modal>
    );
}

